// 表情タブ: プレビュー + シェイプキースライダー（検索フィルタ付き）+ クリップ一覧。
//
// スライダー操作では全体の再描画を行わない（フォーカス喪失と重い再構築を避けるため）。
// 値は store.editor.values を直接更新し、プレビューと数値入力欄だけをその場で書き換える。
//
// 「使用中」セクションと本体リストには同じシェイプキーの行が同時に出るため、
// 同じキーの行同士を rowRegistry で同期させる。

import { describeError } from "../core/errors.ts";
import { shapeKeyOf, type ClipKeyframe, type TrackingSetting } from "../core/types.ts";
import { store } from "../store/state.ts";
import { confirmDialog, el, modal, openMenu, promptText, storageGet, storageSet } from "./dom.ts";
import { attachPreview, refreshPreview } from "./preview.ts";
import { openClipPackReceiveDialog, openClipPackSendDialog } from "./transferView.ts";

/**
 * 有効なシェイプ数の表示。スライダー操作では全体を再描画しないため、
 * このラベルだけをその場で更新する。
 */
let countLabel: HTMLElement | null = null;
/** 「使用中」セクションの差し替え先 */
let usedHost: HTMLElement | null = null;

interface RowHandle {
  slider: HTMLInputElement;
  numberInput: HTMLInputElement;
  row: HTMLElement;
}

/** 数値入力欄で許容する範囲（明らかに異常な値はここでクランプ/拒否する） */
const NUMBER_INPUT_MIN = -1000;
const NUMBER_INPUT_MAX = 1000;
/** shapeKey → その画面内に存在する行（使用中セクションと本体リストで重複しうる） */
let rowRegistry = new Map<string, RowHandle[]>();

function countText(): string {
  const editing = store.clipByIndex(store.editor.editingClipIndex);
  const n = store.editor.values.size;
  return editing ? `編集中: ${editing.name}（${n}個）` : `新規表情（${n}個）`;
}

function updateCount(): void {
  if (countLabel) countLabel.textContent = countText();
}

/** 同じシェイプキーの他の行へ値を反映する */
function syncRows(key: string, value: number, except: RowHandle): void {
  for (const h of rowRegistry.get(key) ?? []) {
    if (h === except) continue;
    // スライダーの value は 0〜100 に自動クランプされるが、内部状態（store.editor.values）は
    // 数値入力欄の生の値を正とするため、スライダー側の見た目だけの制約として扱う。
    h.slider.value = String(value);
    h.numberInput.value = String(value);
    h.row.classList.toggle("active", value > 0);
  }
}

function setValue(key: string, value: number, from: RowHandle): void {
  if (value === 0) store.editor.values.delete(key);
  else store.editor.values.set(key, value);
  from.row.classList.toggle("active", value > 0);
  syncRows(key, value, from);
  updateCount();
  refreshPreview();
}

function shapeRow(meshIndex: number, name: string): HTMLElement {
  const key = shapeKeyOf(meshIndex, name);
  const initial = store.editor.values.get(key) ?? 0;

  const row = el("div", { class: `shape-item${initial > 0 ? " active" : ""}` });

  const slider = el("input", {
    type: "range",
    min: "0",
    max: "100",
    step: "0.1",
    value: String(initial),
    "aria-label": name,
  });
  // 数値入力欄: -1000〜1000 を許容し、スライダーのレンジを超える値も扱えるようにする。
  // 小数点入力も可能（丸め処理はしていない）ため、placeholder/title で明示しておく。
  const numberInput = el("input", {
    type: "number",
    class: "shape-num-input",
    min: String(NUMBER_INPUT_MIN),
    max: String(NUMBER_INPUT_MAX),
    step: "0.1",
    value: String(initial),
    placeholder: "例: 33.5",
    title: "小数点も入力できます（例: 33.5）",
    "aria-label": `${name} の数値（小数点も入力できます）`,
  }) as HTMLInputElement;
  const handle: RowHandle = { slider, numberInput, row };
  const list = rowRegistry.get(key);
  if (list) list.push(handle);
  else rowRegistry.set(key, [handle]);

  slider.addEventListener("input", () => {
    numberInput.value = slider.value;
    setValue(key, Number(slider.value), handle);
  });
  // ドラッグ終了時にだけ「使用中」セクションを組み直す（ドラッグ中の再構築を避ける）
  slider.addEventListener("change", () => refreshUsedSection());

  numberInput.addEventListener("input", () => {
    let v = Number(numberInput.value);
    if (!Number.isFinite(v)) return;
    v = clampNum(v, NUMBER_INPUT_MIN, NUMBER_INPUT_MAX);
    if (String(v) !== numberInput.value) numberInput.value = String(v);
    // スライダーのつまみは 0〜100 の範囲で頭打ちにする（内部状態は numberInput の値が正）
    slider.value = String(v);
    setValue(key, v, handle);
  });
  numberInput.addEventListener("change", () => refreshUsedSection());

  row.appendChild(
    el(
      "div",
      { class: "head" },
      el("span", { class: "name", text: name, title: name }),
      numberInput,
      el("button", {
        class: "icon-btn",
        text: "↺",
        "aria-label": `${name} を 0 に戻す`,
        onclick: () => {
          slider.value = "0";
          numberInput.value = "0";
          setValue(key, 0, handle);
          refreshUsedSection();
        },
      }),
    ),
  );
  row.appendChild(slider);
  return row;
}

/** 値が 0 でないシェイプキーだけを上部に固定表示する「使用中」セクション */
function usedSection(): HTMLElement {
  const avatar = store.avatar;
  const box = el("div", { class: "section used-section" });
  if (!avatar) return box;

  const used = store.editorUsedShapeKeys();
  const entries: Array<{ meshIndex: number; name: string }> = [];
  for (const mesh of avatar.face.meshes) {
    for (const n of mesh.blendshapeNames) {
      if (used.has(shapeKeyOf(mesh.index, n))) entries.push({ meshIndex: mesh.index, name: n });
    }
  }

  box.appendChild(
    el(
      "div",
      { class: "row wrap" },
      el("h2", { text: `使用中（${entries.length}）`, style: { margin: "0" } }),
      el("span", { class: "spacer" }),
      entries.length > 0
        ? el("button", {
            class: "btn small ghost",
            text: "全部0に",
            onclick: () => {
              store.clearShapeValues();
              refreshPreview();
            },
          })
        : null,
    ),
  );

  if (entries.length === 0) {
    box.appendChild(el("p", { class: "muted", text: "値が 0 でないシェイプキーはまだありません。下の一覧から動かすとここに集まります。" }));
    return box;
  }

  const list = el("div", { class: "shape-list" });
  for (const e of entries) list.appendChild(shapeRow(e.meshIndex, e.name));
  box.appendChild(list);
  return box;
}

function refreshUsedSection(): void {
  if (!usedHost) return;
  // 差し替えるノードに属する行の登録を捨ててから作り直す
  const stale = new Set<HTMLElement>();
  usedHost.querySelectorAll(".shape-item").forEach((n) => stale.add(n as HTMLElement));
  for (const [key, handles] of rowRegistry) {
    const kept = handles.filter((h) => !stale.has(h.row));
    if (kept.length === 0) rowRegistry.delete(key);
    else rowRegistry.set(key, kept);
  }
  usedHost.replaceChildren(usedSection());
}

/**
 * シェイプキー名の命名規則から推定する簡易カテゴリ（機能A）。
 * 完全な精度は狙わない（アバターごとに命名規則が異なるため）。マッチしない場合は
 * 必ず "other" に落とし、分類漏れで一覧から消えることがないようにする。
 */
type ShapeCategory = "eyes" | "mouth" | "brow" | "cheek" | "body" | "hair" | "other";

interface CategoryDef {
  id: Exclude<ShapeCategory, "other">;
  label: string;
  keywords: string[];
}

// キーワードは実データ（testdata/RaddollV3.fmavatar、シェイプキー約320個）で分類結果を
// 目視確認した上で調整済み（単純な部分一致・語境界チェックなし）。例えば「brow」に語境界
// チェックを入れると "Eyebrow" が拾えなくなるなど、複合語（Eyebrow/Eyelids等）を取りこぼす
// 副作用の方が大きかったため、単純な部分一致を採用している。
const CATEGORY_DEFS: CategoryDef[] = [
  { id: "eyes", label: "目", keywords: ["eye", "blink", "wink", "pupil", "目", "瞬き", "瞳", "まばたき", "ウィンク"] },
  { id: "brow", label: "眉", keywords: ["brow", "眉"] },
  {
    id: "mouth",
    label: "口",
    keywords: ["mouth", "lip", "viseme", "vrc.v_", "jaw", "tongue", "口", "あ", "い", "う", "え", "お"],
  },
  { id: "cheek", label: "頬", keywords: ["cheek", "blush", "頬"] },
  { id: "body", label: "身体", keywords: ["body", "breast", "bust", "hip", "waist", "leg", "arm", "身体", "胸", "腰"] },
  { id: "hair", label: "髪", keywords: ["hair", "髪"] },
];

const CATEGORY_CHIP_ITEMS: Array<{ id: ShapeCategory | "all"; label: string }> = [
  { id: "all", label: "すべて" },
  ...CATEGORY_DEFS.map((d) => ({ id: d.id, label: d.label })),
  { id: "other", label: "その他" },
];

// "eyebrow" 等、"eye" を部分文字列に含む複合語が誤って「目」に分類されないよう、
// 表示順（CATEGORY_DEFS）とは別に、判定時だけ「眉」を「目」より先にチェックする
const CATEGORY_MATCH_ORDER: CategoryDef[] = [...CATEGORY_DEFS].sort((a, b) =>
  a.id === "brow" ? -1 : b.id === "brow" ? 1 : 0
);

function categoryOf(name: string): ShapeCategory {
  const lower = name.toLowerCase();
  for (const def of CATEGORY_MATCH_ORDER) {
    if (def.keywords.some((k) => lower.includes(k.toLowerCase()))) return def.id;
  }
  return "other";
}

/** 再描画をまたいで選択中のカテゴリフィルタを保つ */
let categoryFilter: ShapeCategory | "all" = "all";

/** カテゴリ絞り込みチップ（すべて/目/眉/口/頬/身体/髪/その他）。選択すると一覧を絞り込む。 */
function categoryChips(rebuildList: () => void): HTMLElement {
  const chips = el("div", { class: "category-chips", role: "tablist" });
  for (const item of CATEGORY_CHIP_ITEMS) {
    chips.appendChild(
      el("button", {
        type: "button",
        class: `chip${categoryFilter === item.id ? " active" : ""}`,
        text: item.label,
        role: "tab",
        "aria-selected": categoryFilter === item.id ? "true" : "false",
        onclick: () => {
          if (categoryFilter === item.id) return;
          categoryFilter = item.id;
          rebuildList();
          chips.replaceWith(categoryChips(rebuildList));
        },
      }),
    );
  }
  return chips;
}

/** 再描画をまたいでメッシュごとの折りたたみ状態を保つ（閉じたメッシュの index を保持） */
const collapsedMeshGroups = new Set<number>();
/** クリップ一覧の「選択してエクスポート」モード中かどうか */
let clipSelectMode = false;
/** 選択中のクリップ index */
const selectedClipIndices = new Set<number>();

/** シェイプキーを持つメッシュの index 一覧（折りたたみグループの対象） */
function meshGroupIndices(avatar: NonNullable<typeof store.avatar>): number[] {
  return avatar.face.meshes.filter((m) => m.blendshapeNames.length > 0).map((m) => m.index);
}

function shapeList(filter: string): HTMLElement {
  const avatar = store.avatar;
  const container = el("div", { class: "shape-list" });
  if (!avatar) return container;

  const q = filter.trim().toLowerCase();
  const searching = q !== "";
  let shown = 0;

  const multiMesh = meshGroupIndices(avatar).length > 1;

  for (const mesh of avatar.face.meshes) {
    // シェイプキーを持たないメッシュ（髪・アクセサリ等）は一覧に出さない
    if (mesh.blendshapeNames.length === 0) continue;
    const names = mesh.blendshapeNames.filter((n) => {
      if (categoryFilter !== "all" && categoryOf(n) !== categoryFilter) return false;
      return q === "" || n.toLowerCase().includes(q);
    });
    if (names.length === 0) continue;

    const rows = names.map((n) => {
      shown++;
      return shapeRow(mesh.index, n);
    });

    if (!multiMesh) {
      for (const row of rows) container.appendChild(row);
      continue;
    }

    const open = searching || !collapsedMeshGroups.has(mesh.index);
    const group = el(
      "details",
      {
        class: "mesh-group",
        open,
        ontoggle: (e: Event) => {
          if ((e.target as HTMLDetailsElement).open) collapsedMeshGroups.delete(mesh.index);
          else collapsedMeshGroups.add(mesh.index);
        },
      },
      el("summary", { text: `${mesh.name}（${names.length}）` }),
      ...rows,
    );
    container.appendChild(group);
  }

  if (shown === 0) {
    container.appendChild(el("p", { class: "muted", text: "該当するシェイプキーがありません。" }));
  }
  return container;
}

/** 再描画をまたいで「詳細設定」の開閉状態を保つ */
let trackingOpen = false;

/** Tracking Control（まばたき / 口）の詳細設定 */
function trackingSection(): HTMLElement {
  const t = store.editor.tracking;
  const isDefault = t.eyes === "animation" && t.mouth === "expression";

  const radioGroup = <K extends keyof TrackingSetting>(
    field: K,
    label: string,
    options: Array<{ value: TrackingSetting[K]; label: string; hint: string }>,
  ): HTMLElement => {
    const group = el("div", { class: "field" }, el("label", { text: label }));
    for (const o of options) {
      const id = `tracking-${field}-${String(o.value)}`;
      const input = el("input", {
        type: "radio",
        name: `tracking-${field}`,
        id,
        value: String(o.value),
        onchange: () => store.setEditorTracking({ [field]: o.value } as Partial<TrackingSetting>),
      });
      if (t[field] === o.value) input.checked = true;
      group.appendChild(
        el(
          "label",
          { class: "radio-row", for: id },
          input,
          el("span", {}, el("span", { class: "r-title", text: o.label }), el("span", { class: "r-hint", text: o.hint })),
        ),
      );
    }
    return group;
  };

  const details = el(
    "details",
    { class: "advanced", open: trackingOpen },
    el(
      "summary",
      {},
      "詳細設定（まばたき / 口）",
      el("span", { class: "notyet", text: isDefault ? "既定" : "変更あり" }),
    ),
    el("p", {
      class: "muted",
      text: "この表情が出ているあいだ、自動まばたきとリップシンクをどう扱うかを設定します（Tracking Control）。設定はクリップごとに保存され、割り当てたジェスチャー・コンビネーションすべてに反映されます。",
    }),
    radioGroup("eyes", "まばたき", [
      { value: "animation", label: "アニメーション優先", hint: "表情の目の形を保つ（自動まばたきを止める）" },
      { value: "tracking", label: "トラッキング維持", hint: "自動まばたきをそのまま残す" },
    ]),
    radioGroup("mouth", "口", [
      { value: "expression", label: "表情優先", hint: "しゃべっても表情の口の形を保つ" },
      { value: "lipsync", label: "リップシンク維持", hint: "しゃべりを優先（口のシェイプは適用時に除外）" },
    ]),
  );
  details.addEventListener("toggle", () => {
    trackingOpen = details.open;
  });
  return details;
}

/** 再描画をまたいで「キーフレーム」の開閉状態を保つ */
let keyframeOpen = false;
/** 「延長」ボタンでユーザーが手動で広げたタイムライン表示範囲（秒） */
let timelineExtraRange = 0;

function clampNum(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

/** Unity側で.animクリップを生成する際のフレームレート（FmFxBuilder.cs の Fps と一致させる） */
const CLIP_FPS = 30;
/** キーフレーム時刻の刻み幅（クリップのフレーム数単位）の選択肢。Unityのアニメーションクリップエディタと
 * 同様、1フレーム単位まで選べるようにしつつ、粗い刻みも選べるようにする。 */
const STEP_FRAME_OPTIONS = [1, 3, 15, 30] as const;
const LS_STEP_FRAMES_KEY = "facemaker.keyframeStepFrames";

function loadStepFrames(): number {
  const raw = Number(storageGet(LS_STEP_FRAMES_KEY));
  return (STEP_FRAME_OPTIONS as readonly number[]).includes(raw) ? raw : 3;
}

/** 再描画をまたいで保持するキーフレームの刻み幅（フレーム数）。既定は3フレーム(0.1秒相当)。 */
let keyframeStepFrames = loadStepFrames();

function stepSeconds(): number {
  return keyframeStepFrames / CLIP_FPS;
}

/** 現在の刻み幅で意味のある表示に必要な小数桁数 */
function timeDecimals(): number {
  if (keyframeStepFrames >= CLIP_FPS) return 0;
  if (keyframeStepFrames >= 3) return 1;
  return 2;
}

/** 時刻を選択中の刻み幅（既定 3 フレーム = 0.1 秒）に丸める */
function roundTime(t: number): number {
  const step = stepSeconds();
  return Math.round(Math.round(t / step) * step * 1000) / 1000;
}

/** 0:01.5 のような Unity タイムコード風の表示に整形する（刻み幅に応じて小数桁数を変える） */
function formatTimecode(t: number): string {
  const total = Math.max(0, roundTime(t));
  const m = Math.floor(total / 60);
  const sec = total - m * 60;
  const d = timeDecimals();
  const secStr = sec.toFixed(d);
  const dot = secStr.indexOf(".");
  const intPart = dot === -1 ? secStr : secStr.slice(0, dot);
  const decPart = dot === -1 ? "" : secStr.slice(dot);
  return `${m}:${intPart.padStart(2, "0")}${decPart}`;
}

/**
 * ドラッグでキーフレームの時刻を移動する。store には「時刻ごとの記録」しか
 * ないので、移動対象の中身（shapes）を退避してから removeKeyframe→addKeyframe
 * を行い、エディタの現在値（store.editor.values）は変更前の状態に戻す。
 */
function moveKeyframe(clip: { index: number }, oldTime: number, newTime: number): void {
  if (Math.abs(oldTime - newTime) < 1e-6) return;
  const c = store.clipByIndex(clip.index);
  const kf = c?.keyframes?.find((k) => Math.abs(k.time - oldTime) < 1e-6);
  if (!c || !kf) return;
  const shapesCopy = kf.shapes.map((s) => ({ ...s }));
  const savedValues = new Map(store.editor.values);
  const savedEditingIndex = store.editor.editingClipIndex;
  store.editor.values = new Map(shapesCopy.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
  store.editor.editingClipIndex = c.index;
  store.removeKeyframe(c.index, oldTime);
  store.addKeyframe(c.index, newTime);
  store.editor.values = savedValues;
  store.editor.editingClipIndex = savedEditingIndex;
  store.setKeyframeTime(newTime);
}

/**
 * キーフレーム編集（M5）。Unity のアニメーションウィンドウ風の
 * タイムライントラック UI。編集中の保存済みクリップに対して、
 * 「今のスライダーの状態」を指定時刻のキーフレームとして記録していく方式。
 */
/** 未保存の新規クリップ状態でキーフレームを打とうとしたとき、先にクリップとして保存させる。 */
async function ensureClipForKeyframe(): Promise<number | null> {
  const existing = store.clipByIndex(store.editor.editingClipIndex);
  if (existing) return existing.index;
  if (!store.project) return null;
  const name = await promptText(
    "クリップとして保存",
    "クリップ名",
    store.suggestClipName(),
    (v) => {
      if (v.length === 0) return "名前を入力してください。";
      if (/[\\/:*?"<>|]/.test(v)) return `\\ / : * ? " < > | は使えません。`;
      if (!store.isNameAvailable(v, store.editor.editingClipIndex)) return "同じ名前のクリップが既にあります。";
      return null;
    },
  );
  if (name === null) return null;
  const clip = store.saveAsClip(name);
  return clip.index;
}

function keyframeSection(): HTMLElement | null {
  if (!store.project) return null;
  const clip = store.clipByIndex(store.editor.editingClipIndex);

  const kfs = clip?.keyframes ?? [];
  const kfMax = kfs.length > 0 ? kfs[kfs.length - 1].time : 0;
  const rangeMax = Math.max(kfMax + 1, store.editor.keyframeTime + 1, 5 + timelineExtraRange);

  const timeToPercent = (t: number): number => clampNum(t / rangeMax, 0, 1) * 100;
  const percentToTime = (ratio: number): number => roundTime(clampNum(ratio, 0, 1) * rangeMax);

  const timeInput = el("input", {
    class: "text-input",
    type: "number",
    min: "0",
    max: "60",
    step: String(stepSeconds()),
    value: String(roundTime(store.editor.keyframeTime)),
    style: { maxWidth: "100px" },
  }) as HTMLInputElement;
  timeInput.addEventListener("change", () => {
    const v = roundTime(Number(timeInput.value));
    timeInput.value = String(v);
    if (Number.isFinite(v) && v >= 0) store.setKeyframeTime(v);
  });

  const stepSelect = el(
    "select",
    {
      class: "select",
      title: "キーフレームの時刻を打てる最小単位（Unityのアニメーションクリップは30fpsで書き出されます）",
      onchange: (e: Event) => {
        keyframeStepFrames = Number((e.target as HTMLSelectElement).value);
        storageSet(LS_STEP_FRAMES_KEY, String(keyframeStepFrames));
        const fresh = keyframeSection();
        if (fresh) details.replaceWith(fresh);
      },
    },
    ...STEP_FRAME_OPTIONS.map((f) =>
      el("option", {
        value: String(f),
        selected: f === keyframeStepFrames,
        text: f === CLIP_FPS ? "1秒刻み" : `1/${CLIP_FPS / f}秒刻み（${f}フレーム）`,
      }),
    ),
  ) as HTMLSelectElement;

  // --- タイムライントラック ---
  const track = el("div", { class: "kf-track" }) as HTMLDivElement;

  // 目盛り
  const ruler = el("div", { class: "kf-ruler" });
  const tickCount = Math.ceil(rangeMax);
  for (let s = 0; s <= tickCount; s++) {
    ruler.appendChild(
      el("span", {
        class: "kf-tick",
        text: formatTimecode(s),
        style: { left: `${timeToPercent(s)}%` },
      }),
    );
  }
  track.appendChild(ruler);

  const lane = el("div", { class: "kf-lane" }) as HTMLDivElement;
  track.appendChild(lane);

  // 再生ヘッド
  const playhead = el("div", {
    class: "kf-playhead",
    style: { left: `${timeToPercent(store.editor.keyframeTime)}%` },
  }) as HTMLDivElement;
  lane.appendChild(playhead);

  // キーフレームマーカー
  if (clip) {
    for (const kf of kfs) {
      lane.appendChild(keyframeMarker({ index: clip.index }, kf, timeToPercent(kf.time), lane, rangeMax));
    }
  }

  // トラック（レーン）クリック/ドラッグで再生ヘッドを移動
  let scrubbing = false;
  const scrubTo = (clientX: number): void => {
    const rect = lane.getBoundingClientRect();
    if (rect.width <= 0) return;
    const t = percentToTime((clientX - rect.left) / rect.width);
    playhead.style.left = `${timeToPercent(t)}%`;
    timeInput.value = String(t);
    // ドラッグ中は store.editor.values を直接差し替えて 3D プレビューだけ更新する
    // （store.setKeyframeTime は毎回全体再描画してしまい、ドラッグ中の pointer capture が壊れるため）。
    if (clip) {
      store.editor.values = store.previewValuesAt(clip.index, t);
      refreshPreview();
    }
  };
  lane.addEventListener("pointerdown", (e) => {
    if ((e.target as HTMLElement).closest(".kf-marker")) return;
    scrubbing = true;
    lane.setPointerCapture(e.pointerId);
    scrubTo(e.clientX);
  });
  lane.addEventListener("pointermove", (e) => {
    if (!scrubbing) return;
    scrubTo(e.clientX);
  });
  const finishScrub = (e: PointerEvent): void => {
    if (!scrubbing) return;
    scrubbing = false;
    lane.releasePointerCapture(e.pointerId);
    store.setKeyframeTime(Number(timeInput.value) || 0);
  };
  lane.addEventListener("pointerup", finishScrub);
  lane.addEventListener("pointercancel", finishScrub);

  const recordBtn = el("button", {
    class: "btn small primary kf-record-btn",
    text: "＋ この時刻に記録",
    onclick: () => {
      void (async () => {
        const clipIndex = await ensureClipForKeyframe();
        if (clipIndex === null) return;
        const t = roundTime(Number(timeInput.value) || 0);
        store.addKeyframe(clipIndex, t);
        store.setBanner({
          kind: "info",
          title: `${formatTimecode(t)} にキーフレームを記録しました`,
          detail: "同じ時刻に記録すると上書きされます。",
          hint: null,
        });
      })();
    },
  });

  const extendBtn = el("button", {
    class: "btn small ghost",
    text: "延長 +5秒",
    onclick: () => {
      timelineExtraRange += 5;
      store.notify();
    },
  });

  const loopBox = el("input", { type: "checkbox", id: "kf-loop" }) as HTMLInputElement;
  loopBox.checked = clip?.loop === true;
  loopBox.addEventListener("change", () => {
    if (clip) store.setClipLoop(clip.index, loopBox.checked);
  });

  const canPlay = clip?.type === "keyframe" && (clip.keyframes?.length ?? 0) > 1;
  const isPlaying = canPlay && store.playingClipIndex === clip?.index;

  const startPlay = (fromTime: number): void => {
    if (!clip) return;
    store.playingClipIndex = clip.index;
    store.playStartTime = fromTime;
    store.playSession += 1;
    store.notify();
    refreshPreview();
  };
  const stopPlay = (): void => {
    store.playingClipIndex = null;
    store.notify();
    refreshPreview();
  };

  const playHereBtn = el("button", {
    class: `btn small${isPlaying ? " primary" : " ghost"}`,
    text: isPlaying ? "■ 停止" : "⭮ 再生（現在位置から）",
    disabled: !canPlay,
    title: canPlay ? "今のタイムライン位置からループ再生します" : "キーフレームが2枚以上必要です",
    onclick: () => {
      if (isPlaying) stopPlay();
      else startPlay(store.editor.keyframeTime);
    },
  });
  const playFromStartBtn = el("button", {
    class: "btn small ghost",
    text: "⏮ 頭から再生",
    disabled: !canPlay,
    title: canPlay ? "0秒からループ再生します" : "キーフレームが2枚以上必要です",
    onclick: () => startPlay(0),
  });

  const details = el(
    "details",
    { class: "advanced", open: keyframeOpen },
    el(
      "summary",
      {},
      "キーフレーム（時間で動く表情）",
      el("span", { class: "notyet", text: clip?.type === "keyframe" ? `${kfs.length} 枚` : "未使用" }),
    ),
    el("p", {
      class: "muted",
      text: clip
        ? "スライダーで作った状態を、指定した時刻のキーフレームとして記録します。2 枚以上記録すると、このクリップは静止した表情ではなく時間で変化するアニメーションになります（キーフレームの間は直線的に補間されます）。マーカーをタップで読込、ドラッグで時刻を変更、×で削除できます。"
        : "スライダーで作った状態を、指定した時刻のキーフレームとして記録します。まだクリップを保存していない場合は、最初の「＋ この時刻に記録」でクリップ名を聞かれます。2 枚以上記録すると時間で変化するアニメーションになります。",
    }),
    el("div", { class: "kf-timeline" }, track),
    el(
      "div",
      { class: "row wrap", style: { gap: "8px", alignItems: "center", marginTop: "8px" } },
      el("label", { text: "時刻（秒）" }),
      timeInput,
      stepSelect,
      el("span", { class: "spacer" }),
      playFromStartBtn,
      playHereBtn,
      extendBtn,
      recordBtn,
    ),
    kfs.length === 0
      ? el("p", { class: "muted", text: "まだキーフレームがありません。記録するとこのクリップは時間軸アニメーションになります。" })
      : null,
    clip?.type === "keyframe"
      ? el("label", { class: "radio-row", for: "kf-loop" }, loopBox, el("span", {}, el("span", { class: "r-title", text: "ループ再生" }), el("span", { class: "r-hint", text: "表情が出ているあいだ繰り返す" })))
      : null,
  );
  details.addEventListener("toggle", () => {
    keyframeOpen = details.open;
  });
  return details;
}

/** タイムライン上の1つのキーフレームマーカー（ダイヤモンド型）。クリックで読込、ドラッグで移動、×で削除。 */
function keyframeMarker(
  clip: { index: number },
  kf: ClipKeyframe,
  initialPercent: number,
  lane: HTMLDivElement,
  rangeMax: number,
): HTMLElement {
  const selected = Math.abs(store.editor.keyframeTime - kf.time) < 1e-6;
  const marker = el("button", {
    type: "button",
    class: `kf-marker${selected ? " selected" : ""}`,
    style: { left: `${initialPercent}%` },
    "aria-label": `${formatTimecode(kf.time)} のキーフレーム（${kf.shapes.length}個）`,
  }) as HTMLButtonElement;

  // 誤タップでの即削除を防ぐため、このマーカーが選択済み（一度タップ済み）のときだけ×を機能させる。
  const delBadge = el("span", {
    class: `kf-marker-del${selected ? "" : " inert"}`,
    text: "×",
    "aria-label": selected ? "このキーフレームを削除" : "先にこのキーフレームをタップして選択してください",
    onclick: (e: Event) => {
      e.stopPropagation();
      if (!selected) return;
      store.removeKeyframe(clip.index, kf.time);
    },
  });
  // ×の pointerdown がマーカーへ伝わると、マーカー側が setPointerCapture してしまい
  // 以降の pointerup/click がマーカーに向く（×の click が発火しない）うえ、
  // pointerup で loadKeyframe → 再描画が走って×が DOM から消える。×の上では伝播を止める。
  delBadge.addEventListener("pointerdown", (e) => {
    e.stopPropagation();
  });
  delBadge.addEventListener("pointerup", (e) => {
    e.stopPropagation();
  });
  marker.appendChild(delBadge);
  marker.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    if (!selected) return;
    store.removeKeyframe(clip.index, kf.time);
  });

  let dragging = false;
  let moved = false;
  let startX = 0;
  let currentTime = kf.time;

  marker.addEventListener("pointerdown", (e) => {
    dragging = true;
    moved = false;
    startX = e.clientX;
    currentTime = kf.time;
    marker.setPointerCapture(e.pointerId);
    e.stopPropagation();
  });
  marker.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    if (Math.abs(e.clientX - startX) > 3) moved = true;
    const rect = lane.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = clampNum((e.clientX - rect.left) / rect.width, 0, 1);
    currentTime = roundTime(clampNum(ratio * rangeMax, 0, rangeMax));
    marker.style.left = `${clampNum(currentTime / rangeMax, 0, 1) * 100}%`;
  });
  const finish = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    marker.releasePointerCapture(e.pointerId);
    if (moved) {
      moveKeyframe(clip, kf.time, currentTime);
    } else {
      store.loadKeyframe(clip.index, kf.time);
      refreshPreview();
    }
  };
  marker.addEventListener("pointerup", finish);
  marker.addEventListener("pointercancel", () => {
    dragging = false;
  });

  return marker;
}

function clipPackFileInput(): HTMLInputElement {
  return el("input", {
    type: "file",
    accept: ".json,.fmclippack.json",
    style: { display: "none" },
    onchange: (e: Event) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = "";
      if (file) void importClipPackFile(file);
    },
  }) as HTMLInputElement;
}

async function importClipPackFile(file: File): Promise<void> {
  try {
    const text = await file.text();
    const { addedCount, missingShapeNames } = store.importClipPack(text);
    const detail =
      missingShapeNames.length > 0
        ? `一致しないシェイプキー ${missingShapeNames.length} 件は無視されました: ${missingShapeNames.join(", ")}`
        : "";
    store.setBanner({ kind: "info", title: `${addedCount} 件のクリップを読み込みました`, detail, hint: null });
  } catch (e) {
    const d = describeError(e);
    store.setBanner({ kind: "error", title: d.title, detail: d.detail, hint: d.hint });
  }
}

function clipListSection(): HTMLElement {
  const project = store.project;
  const section = el("div", { class: "section" });

  const pending = store.pendingExistingClipCount();
  const importInput = clipPackFileInput();
  section.appendChild(
    el(
      "div",
      { class: "row wrap" },
      el("h2", { text: "クリップ一覧", style: { margin: "0" } }),
      el("span", { class: "spacer" }),
      pending > 0
        ? el("button", {
            class: "btn small",
            text: `既存クリップを取り込む（${pending}）`,
            onclick: () => {
              const n = store.importExistingClips();
              store.setBanner({
                kind: "info",
                title: `既存クリップを ${n} 個取り込みました`,
                detail: "アバターの FX から解析された表情クリップです。編集・複製して使えます。",
                hint: null,
              });
            },
          })
        : null,
      el("button", {
        class: "btn small ghost",
        text: "クリップを読み込む",
        onclick: () => importInput.click(),
      }),
      importInput,
      el("button", {
        class: "btn small ghost",
        text: "コードで受信",
        onclick: () => openClipPackReceiveDialog(),
      }),
      project && project.clips.length > 0
        ? el("button", {
            class: "btn small ghost",
            text: clipSelectMode ? "選択をキャンセル" : "選択してエクスポート",
            onclick: () => {
              clipSelectMode = !clipSelectMode;
              selectedClipIndices.clear();
              store.notify();
            },
          })
        : null,
    ),
  );

  if (store.avatar && store.avatar.face.existingClips.length > 0 && pending === 0) {
    section.appendChild(
      el("p", {
        class: "muted",
        text: `アバターの既存クリップ ${store.avatar.face.existingClips.length} 個は読み込み時に取り込み済みです。`,
      }),
    );
  }

  if (!project || project.clips.length === 0) {
    section.appendChild(el("p", { class: "muted", text: "まだクリップがありません。スライダーで表情を作って「クリップとして保存」を押してください。" }));
    return section;
  }

  section.appendChild(
    el("p", {
      class: "muted",
      text: "クリップだけを転送したい場合は、各クリップの ⋮ メニューから「エクスポート」または「リレーで送信」が使えます。",
    }),
  );

  if (clipSelectMode) {
    section.appendChild(
      el(
        "div",
        { class: "row wrap", style: { gap: "8px", margin: "4px 0" } },
        el("button", {
          class: "btn small primary",
          text: `選択した${selectedClipIndices.size}件をエクスポート`,
          onclick: () => {
            if (selectedClipIndices.size === 0) return;
            store.exportClipsAsPack([...selectedClipIndices]);
            clipSelectMode = false;
            selectedClipIndices.clear();
            store.notify();
          },
        }),
        el("button", {
          class: "btn small",
          text: `選択した${selectedClipIndices.size}件をリレーで送信`,
          onclick: () => {
            if (selectedClipIndices.size === 0) return;
            openClipPackSendDialog([...selectedClipIndices]);
            clipSelectMode = false;
            selectedClipIndices.clear();
            store.notify();
          },
        }),
      ),
    );
  }

  for (const clip of project.clips) {
    const isEditing = store.editor.editingClipIndex === clip.index;
    const menuBtn = el("button", { class: "icon-btn menu-trigger", text: "⋮", "aria-label": `${clip.name} の操作` });
    menuBtn.addEventListener("click", () => void openClipMenu(clip.index, clip.name, menuBtn));
    const checkbox = clipSelectMode
      ? el("input", {
          type: "checkbox",
          checked: selectedClipIndices.has(clip.index),
          onchange: (e: Event) => {
            if ((e.target as HTMLInputElement).checked) selectedClipIndices.add(clip.index);
            else selectedClipIndices.delete(clip.index);
            store.notify();
          },
        })
      : null;
    section.appendChild(
      el(
        "div",
        { class: `clip-item${isEditing ? " editing" : ""}` },
        checkbox,
        el("span", { class: "name", text: clip.name, title: clip.name }),
        el("span", {
          class: "count",
          text: clip.type === "keyframe" ? `KF ${clip.keyframes?.length ?? 0}枚` : `${clip.shapes.length}個`,
        }),
        clipSelectMode ? null : menuBtn,
      ),
    );
  }
  return section;
}

async function openClipMenu(clipIndex: number, clipName: string, anchor: HTMLElement): Promise<void> {
  const choice = await openMenu(
    [
      { label: "編集", value: "edit" },
      { label: "複製", value: "duplicate" },
      { label: "エクスポート", value: "export" },
      { label: "リレーで送信", value: "send" },
      { label: "削除", value: "delete", danger: true },
    ],
    anchor,
  );
  if (choice === "edit") {
    store.editClip(clipIndex);
  } else if (choice === "duplicate") {
    store.duplicateClip(clipIndex);
  } else if (choice === "export") {
    store.exportClipsAsPack([clipIndex]);
  } else if (choice === "send") {
    openClipPackSendDialog([clipIndex]);
  } else if (choice === "delete") {
    const ok = await confirmDialog("クリップを削除", `「${clipName}」を削除します。ジェスチャー割り当ても解除されます。`, "削除する");
    if (ok) store.deleteClip(clipIndex);
  }
}

/** 既存クリップを土台にして新規クリップの編集を始める */
function openStartFromClip(): void {
  const project = store.project;
  if (!project) return;
  const body = el("div", {});
  body.appendChild(
    el("p", {
      class: "muted",
      text: "選んだクリップの内容をコピーして「新規クリップ」として編集を始めます。元のクリップは変更されません。保存時に別名で新しいクリップになります。",
    }),
  );
  if (project.clips.length === 0) {
    body.appendChild(el("p", { class: "muted", text: "複製できるクリップがありません。" }));
  }
  for (const clip of project.clips) {
    body.appendChild(
      el(
        "button",
        {
          class: "pick-item",
          onclick: () => {
            store.startNewFromClip(clip.index);
            handle.close();
          },
        },
        el("span", { text: clip.name }),
        el("span", { class: "sub", text: `${clip.shapes.length}個` }),
      ),
    );
  }
  const handle = modal("既存クリップから新規作成", body);
}

async function saveClip(): Promise<void> {
  if (!store.project) return;
  const editing = store.clipByIndex(store.editor.editingClipIndex);
  const initial = editing ? editing.name : store.suggestClipName();
  const name = await promptText(
    editing ? "クリップ名を変更" : "クリップとして保存",
    "クリップ名",
    initial,
    (v) => {
      if (v.length === 0) return "名前を入力してください。";
      if (/[\\/:*?"<>|]/.test(v)) return `\\ / : * ? " < > | は使えません。`;
      if (!store.isNameAvailable(v, store.editor.editingClipIndex)) return "同じ名前のクリップが既にあります。";
      return null;
    },
    editing ? "このクリップ自体の名前が変わります（別クリップとしては保存されません）。別クリップにしたい場合は「既存クリップから新規作成」を使ってください。" : undefined,
  );
  if (name === null) return;
  try {
    store.saveAsClip(name);
    store.setBanner({ kind: "info", title: `「${name}」を保存しました`, detail: "「ジェスチャー」タブで手の形に割り当てられます。", hint: null });
  } catch (e) {
    const d = describeError(e);
    store.setBanner({ kind: "error", title: d.title, detail: d.detail, hint: d.hint });
  }
}

// --- 機能B: シェイプキー誤タッチ対策の専用スクロールボタン ---
// スマホでシェイプキー一覧をスクロールする際、指がスライダーに触れて誤操作するのを防ぐため、
// 画面端に固定した専用ボタンでスクロールできるようにする。シェイプキー一覧自体には独立した
// スクロールコンテナがなく、ページ全体（.content）がスクロールする構造のため、
// このボタンも .content をスクロールさせる（既存の scroll-top-fab と同じ対象）。

/** シングルタップ時に即座にスクロールする量（px）。シェイプキー行 3〜4 行分の目安。 */
const SHAPE_SCROLL_TAP_PX = 140;
/** 長押し中の連続スクロール速度（px/秒） */
const SHAPE_SCROLL_HOLD_SPEED = 260;
/** この時間（ms）以上押し続けたら「長押し＝連続スクロール」とみなす */
const SHAPE_SCROLL_HOLD_DELAY_MS = 300;

function shapeScrollContainer(): HTMLElement | null {
  return document.querySelector(".content");
}

/**
 * 上下1個ずつのスクロールボタンを作る。
 * pointerdown/pointerup/pointercancel を使い、マウス（PC）でもタッチ（スマホ）でも
 * 同じロジックで「タップ＝固定ステップ」「長押し＝一定速度の連続スクロール」を実現する。
 */
function shapeScrollButton(direction: 1 | -1, label: string, ariaLabel: string): HTMLButtonElement {
  const btn = el("button", {
    type: "button",
    class: "shape-scroll-btn",
    "aria-label": ariaLabel,
    text: label,
  }) as HTMLButtonElement;

  let holdTimer: number | null = null;
  let rafId: number | null = null;
  let continuous = false;
  let lastTs = 0;

  const stopLoop = (): void => {
    if (holdTimer !== null) {
      window.clearTimeout(holdTimer);
      holdTimer = null;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    continuous = false;
  };

  const stepContinuous = (ts: number): void => {
    // 再描画でボタンが DOM から外れた場合は pointerup が届かないので、ここで確実に止める
    if (!btn.isConnected) {
      stopLoop();
      return;
    }
    if (lastTs === 0) lastTs = ts;
    const dt = (ts - lastTs) / 1000;
    lastTs = ts;
    const container = shapeScrollContainer();
    if (container) container.scrollTop += direction * SHAPE_SCROLL_HOLD_SPEED * dt;
    rafId = requestAnimationFrame(stepContinuous);
  };

  const cleanup = (e: PointerEvent): void => {
    if (holdTimer !== null) {
      window.clearTimeout(holdTimer);
      holdTimer = null;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (btn.hasPointerCapture?.(e.pointerId)) btn.releasePointerCapture(e.pointerId);
  };
  /** 押下中かどうか（lostpointercapture が pointerup の後にも来るため、二重処理を防ぐ） */
  let pressed = false;

  btn.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    btn.setPointerCapture?.(e.pointerId);
    stopLoop();
    pressed = true;
    continuous = false;
    lastTs = 0;
    holdTimer = window.setTimeout(() => {
      holdTimer = null;
      if (!pressed || !btn.isConnected) return;
      continuous = true;
      lastTs = 0;
      rafId = requestAnimationFrame(stepContinuous);
    }, SHAPE_SCROLL_HOLD_DELAY_MS);
  });
  const finish = (e: PointerEvent): void => {
    if (!pressed) return;
    pressed = false;
    const wasContinuous = continuous;
    cleanup(e);
    continuous = false;
    if (!wasContinuous) {
      // シングルタップ/クリック: 一定量だけ即座にスクロール（長押しには発展しなかった場合）
      shapeScrollContainer()?.scrollBy({ top: direction * SHAPE_SCROLL_TAP_PX, behavior: "smooth" });
    }
  };
  btn.addEventListener("pointerup", finish);
  btn.addEventListener("pointercancel", (e) => {
    pressed = false;
    cleanup(e);
    continuous = false;
  });
  // ポインターキャプチャを失った（要素が DOM から外れた・OS 側で奪われた等）場合もループを止める
  btn.addEventListener("lostpointercapture", () => {
    pressed = false;
    stopLoop();
  });

  return btn;
}

function shapeScrollButtons(): HTMLElement {
  return el(
    "div",
    { class: "shape-scroll-btns" },
    shapeScrollButton(-1, "▲", "シェイプキー一覧を上へスクロール（長押しで連続スクロール）"),
    shapeScrollButton(1, "▼", "シェイプキー一覧を下へスクロール（長押しで連続スクロール）"),
  );
}

export function renderExpressionView(): HTMLElement {
  rowRegistry = new Map();
  const root = el("div", { class: "view-split" });
  attachPreview(root);

  if (!store.avatar) return root;

  const panel = el("div", { class: "view-panel" });
  root.appendChild(panel);

  const editing = store.clipByIndex(store.editor.editingClipIndex);

  // 検索 + 操作バー（スクロール追従）
  const search = el("input", {
    class: "text-input",
    type: "search",
    placeholder: "シェイプキーを検索",
    value: store.editor.filter,
  });
  const listHost = el("div", {});
  const rebuildList = (): void => {
    // 差し替え対象の行を登録から外す
    const stale = new Set<HTMLElement>();
    listHost.querySelectorAll(".shape-item").forEach((n) => stale.add(n as HTMLElement));
    for (const [key, handles] of rowRegistry) {
      const kept = handles.filter((h) => !stale.has(h.row));
      if (kept.length === 0) rowRegistry.delete(key);
      else rowRegistry.set(key, kept);
    }
    listHost.replaceChildren(shapeList(store.editor.filter));
  };
  search.addEventListener("input", () => {
    store.editor.filter = search.value;
    rebuildList();
  });

  const stickyBar = el("div", { class: "sticky-bar" }, search, categoryChips(rebuildList));
  if (meshGroupIndices(store.avatar).length > 1) {
    stickyBar.appendChild(
      el(
        "div",
        { class: "row", style: { gap: "6px" } },
        el("button", {
          class: "btn small ghost",
          text: "すべて展開",
          onclick: () => {
            collapsedMeshGroups.clear();
            rebuildList();
          },
        }),
        el("button", {
          class: "btn small ghost",
          text: "すべて折りたたむ",
          onclick: () => {
            for (const i of meshGroupIndices(store.avatar!)) collapsedMeshGroups.add(i);
            rebuildList();
          },
        }),
      ),
    );
  }
  panel.appendChild(stickyBar);

  panel.appendChild(
    el(
      "div",
      { class: "section" },
      el(
        "div",
        { class: "row wrap" },
        (countLabel = el("span", { class: "muted", text: countText() })),
        el("span", { class: "spacer" }),
        el("button", {
          class: "btn small ghost",
          text: "全部0に",
          onclick: () => {
            store.clearShapeValues();
            refreshPreview();
          },
        }),
      ),
      el("button", {
        class: "btn primary block",
        text: editing ? "クリップを上書き保存" : "クリップとして保存",
        style: { marginTop: "10px" },
        onclick: () => void saveClip(),
      }),
      el(
        "div",
        { class: "row wrap", style: { marginTop: "8px", gap: "8px" } },
        el("button", {
          class: "btn ghost",
          text: "既存クリップから新規作成",
          title: "選んだクリップの内容を元に、別の新しいクリップとして編集を始めます（元のクリップは変わりません）",
          onclick: openStartFromClip,
        }),
        editing
          ? el("button", {
              class: "btn ghost",
              text: "新規クリップに切り替え",
              onclick: () => {
                store.editor.editingClipIndex = null;
                store.notify();
              },
            })
          : null,
      ),
      trackingSection(),
      keyframeSection(),
    ),
  );

  usedHost = el("div", {});
  usedHost.appendChild(usedSection());
  panel.appendChild(usedHost);

  listHost.classList.add("shape-list-host");
  listHost.appendChild(shapeList(store.editor.filter));
  panel.appendChild(listHost);
  panel.appendChild(clipListSection());

  root.appendChild(shapeScrollButtons());

  root.appendChild(
    el("button", {
      class: "scroll-top-fab",
      type: "button",
      "aria-label": "一番上（クリップを保存）へ戻る",
      onclick: () => {
        document.querySelector(".content")?.scrollTo({ top: 0, behavior: "smooth" });
      },
    }, el("span", { class: "arrow", text: "↑" }), "保存へ"),
  );

  return root;
}
